import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';

import ADGMResponsiveModal from '../../../ADGMResponsiveModal';
import styles from './styles';

const PER_PAGE = 20;

function FilterListDetails(props) {
  const { classes, formData, activeModal, setActiveModal, handleSelectChange, dropdownData } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedOptions, setPaginatedOptions] = useState([]);

  useEffect(() => {
    setPaginatedOptions(take(dropdownData, PER_PAGE * currentPage));
  }, [activeModal?.key, currentPage]);

  const handleRadioChange = useCallback(
    (value, checked) => {
      if (!checked) {
        handleSelectChange(activeModal?.name, value);
      } else {
        handleSelectChange(activeModal?.name, null);
      }
    },
    [activeModal?.name],
  );

  const handleCheckBoxChange = useCallback(
    (value, checked) => {
      const curItems = isArray(formData[activeModal?.name]) ? [...formData[activeModal?.name]] : [];
      if (!checked) {
        const filteredItems = curItems.filter(item => item !== value);
        handleSelectChange(activeModal?.name, filteredItems);
      } else {
        const updatedItems = [...curItems, value];
        handleSelectChange(activeModal?.name, updatedItems);
      }
    },
    [formData, activeModal?.name],
  );

  const fetchNext = useCallback(() => {
    setCurrentPage(page => page + 1);
  }, [currentPage]);

  const isRadioChecked = useCallback(
    value => {
      if (isObject(formData[activeModal?.name]))
        return value.toString() === formData[activeModal?.name]?.value?.toString();
      return value?.toString() === formData[activeModal?.name]?.toString();
    },
    [formData, activeModal?.name],
  );
  const isCheckBoxChecked = useCallback(
    value => {
      if (!isEmpty(formData[activeModal?.name]) && isArray(formData[activeModal?.name]))
        return formData[activeModal?.name].includes(value);
      return false;
    },
    [formData, activeModal?.name],
  );

  return (
    <ADGMResponsiveModal
      open={activeModal}
      toggleModal={() => setActiveModal(null)}
      title={activeModal?.title}
      customClasses={{ content: classes.filterModal }}
    >
      <InfiniteScroll
        dataLength={paginatedOptions?.length}
        next={fetchNext}
        hasMore
        scrollableTarget={`scrollableDiv-${activeModal?.name}`}
      >
        <div id={`scrollableDiv-${activeModal?.name}`} className={classes.filterListDetailsContainer}>
          {!activeModal.multi ? (
            <RadioGroup name={activeModal?.name} className={classes.filterRadio}>
              {paginatedOptions?.map(item => (
                <FormControlLabel
                  value={item.value}
                  checked={isRadioChecked(item.value)}
                  control={
                    <Radio color='primary' onClick={() => handleRadioChange(item.value, isRadioChecked(item.value))} />
                  }
                  label={item.label}
                  labelPlacement='start'
                  className={classNames(classes.filterRadioItem)}
                />
              ))}
            </RadioGroup>
          ) : (
            <FormGroup className={classes.filterCheckBoxes}>
              {paginatedOptions?.map(item => (
                <FormControlLabel
                  label={item.label}
                  control={
                    <Checkbox
                      color='primary'
                      name={item.value}
                      checked={isCheckBoxChecked(item.value)}
                      onChange={e => handleCheckBoxChange(item.value, e.target.checked)}
                    />
                  }
                  className={classes.filterCheckBoxItem}
                />
              ))}
            </FormGroup>
          )}
        </div>
      </InfiniteScroll>
    </ADGMResponsiveModal>
  );
}

FilterListDetails.propTypes = {
  classes: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  activeModal: PropTypes.object,
  setActiveModal: PropTypes.func.isRequired,
  handleSelectChange: PropTypes.func.isRequired,
  dropdownData: PropTypes.array.isRequired,
};

FilterListDetails.defaultProps = {
  activeModal: null,
};

export default withStyles(styles)(memo(FilterListDetails));
